export default class PassportPasswordScheme {
  constructor (auth, options) {
    this.$auth = auth
    this.name = options._name

    this.options = Object.assign({}, DEFAULTS, options)
  }

  _setToken (token) {
    if (this.options.globalToken) {
      // Set Authorization token for all axios requests
      this.$auth.ctx.app.$axios.defaults.headers.common.Accept =
                'application/json'
      this.$auth.ctx.app.$axios.setHeader(this.options.tokenName, token)
    }
  }

  _clearToken () {
    if (this.options.globalToken) {
      // Clear Authorization token for all axios requests
      this.$auth.ctx.app.$axios.setHeader(this.options.tokenName, false)
    }
  }

  mounted () {
    let token = null
    if (this.options.tokenRequired) {
      token = this.$auth.syncToken(this.name)
      if (token) {
        this._setToken(token)
      } else {
        this._clearToken()
      }
    }

    const localStorageAuthState = localStorage.getItem('auth-state')
    if (localStorageAuthState && (!this.options.tokenRequired || token)) {
      const authState = JSON.parse(localStorage.getItem('auth-state'))
      this.$auth.setUser(authState.user)
    } else if (localStorageAuthState) {
      localStorage.removeItem('auth-state')
    }

    return this.$auth.fetchUserOnce()
  }

  async login (args) {
    if (!this.options.endpoints.login) {
      return
    }

    // Ditch any leftover local tokens before attempting to log in
    await this._logoutLocally()

    const requestArgs = {
      ...args,
      data: {
        grant_type: 'password',
        client_id: this.options.client_id,
        client_secret: this.options.client_secret,
        scope: '*',
        ...args.data
      }
    }

    const result = await this.$auth.request(
      requestArgs,
      this.options.endpoints.login
    )

    if (this.options.tokenRequired) {
      const token = this.options.tokenType
        ? this.options.tokenType + ' ' + result
        : result

      this.$auth.setToken(this.name, token)
      this._setToken(token)
    }

    return this.fetchUser()
  }

  async fetchUser (endpoint) {
    // User endpoint is disabled.
    if (!this.options.endpoints.user) {
      this.$auth.setUser({})
      return
    }

    // Token is required but not available
    if (this.options.tokenRequired && !this.$auth.getToken(this.name)) {
      this.$auth.setUser({})
      localStorage.removeItem('auth-state')
      return
    }

    try {
      const user = await this.$auth.requestWith(
        this.name,
        endpoint,
        this.options.endpoints.user
      )
      this.$auth.setUser(user)
    } catch (error) {
      if (error?.response?.status === 401) {
        localStorage.removeItem('auth-state')
        await this._logoutLocally()
        return
      }

      throw error
    }
  }

  async logout (endpoint) {
    // Only connect to logout endpoint if it's configured
    if (this.options.endpoints.logout) {
      await this.$auth
        .requestWith(this.name, endpoint, this.options.endpoints.logout)
        .catch(() => {})
    }

    // But logout locally regardless
    return this._logoutLocally()
  }

  _logoutLocally () {
    if (this.options.tokenRequired) {
      this._clearToken()
    }

    localStorage.removeItem('auth-state')
    return this.$auth.reset()
  }
}

const DEFAULTS = {
  tokenRequired: true,
  tokenType: 'Bearer',
  globalToken: true,
  tokenName: 'Authorization'
}
